import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Grid,
  InputLabel,
  Fade,
} from "@mui/material";
import { Link, useNavigate } from "react-router";
import PasswordField from "../../../components/PasswordField";
import BrandCarousel from "./BrandCarousel";
import { useAppSelector, useAppDispatch } from "../../../redux/store";
import {
  loginWithEmail,
  loginWithMagicLink,
  resetState,
} from "../../../redux/slices/loginSlice";
import { resetProjects } from "../../../redux/slices/projectsSlice";

const SIGN_IN_PHOTOS = [
  "/brands/pmh-photo.webp",
  "/brands/cd-photo.webp",
  "/brands/ca-photo.webp",
  "/brands/bhh-photo.webp",
];

const LoginForm = () => {
  const [fadeIn, setFadeIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [photoIndex, setPhotoIndex] = useState(0);

  const { loading, loginError, magicLinkError, magicLinkSuccess } =
    useAppSelector((state) => state.login) as any;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    setFadeIn(true);

    return () => {
      // Reset the state when the component unmounts
      dispatch(resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => {
      setPhotoIndex((index) => (index + 1) % SIGN_IN_PHOTOS.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, []);

  const handleLogin = useCallback(
    (e: any) => {
      e.preventDefault();

      // Clear any previous errors/messages when attempting new login, and
      // drop the previous session's projects so a client's project list
      // cannot follow the user into an admin login.
      dispatch(resetState());
      dispatch(resetProjects());

      // Validate inputs
      if (!email.trim()) {
        return; // Email is required
      }

      // More robust password check - must have actual content, not just whitespace
      const hasPassword = password && password.trim().length > 0;

      if (hasPassword) {
        // If password is provided, use email/password login
        dispatch(
          loginWithEmail({
            email: email.toLowerCase(),
            password: password.trim(),
            remember,
          }),
        );
      } else {
        // If no password, use magic link
        dispatch(loginWithMagicLink({ email: email.toLowerCase(), remember }));
      }
    },
    [dispatch, email, password, remember],
  );

  // Sync email fields
  const handleEmailChange = useCallback(
    (value: any) => {
      setEmail(value);
      // Clear messages when user starts typing
      if (loginError || magicLinkError || magicLinkSuccess) {
        dispatch(resetState());
      }
    },
    [dispatch, loginError, magicLinkError, magicLinkSuccess],
  );

  const handlePasswordChange = useCallback(
    (value: any) => {
      setPassword(value);
      // Clear messages when user starts typing
      if (loginError || magicLinkError || magicLinkSuccess) {
        dispatch(resetState());
      }
    },
    [dispatch, loginError, magicLinkError, magicLinkSuccess],
  );

  const handleNavigate = useCallback(
    (path: any) => (e: any) => {
      e.preventDefault();

      setFadeIn(false);

      dispatch(resetState());

      setTimeout(() => {
        navigate(path);
      }, 1000);
    },
    [dispatch, navigate, setFadeIn],
  );

  return (
    <Fade in={fadeIn}>
      <Grid
        sx={{ width: "100vw", height: "100vh", overflow: "auto" }}
        container
      >
        <Grid
          size={{ sm: 12, md: 6 }}
          sx={{ display: "flex", alignItems: "center", p: 2 }}
        >
          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{
              maxWidth: 350,
              mx: "auto",
              "& .MuiOutlinedInput-input": {
                p: "4.5px 14px",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
              },
              "& .MuiTextField-root": {
                mt: 0,
              },
              "& .MuiButton-root": {
                borderRadius: 0,
                p: "2.75px 15px",
              },
            }}
          >
            <BrandCarousel />
            <Typography variant="h4" sx={{ fontSize: 24 }} gutterBottom>
              Welcome to Your Client Portal
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: 15, mb: 2 }}
              gutterBottom
            >
              Enter your credentials to securely view and pay your invoices
            </Typography>
            <InputLabel htmlFor="email" sx={{ fontSize: 14 }}>
              Email Address
            </InputLabel>
            <TextField
              id="email"
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              autoComplete="email"
              disabled={loading}
              error={!!(loginError || magicLinkError)}
              placeholder="Enter your email address"
              value={email}
              onChange={(e: any) => handleEmailChange(e.target.value)}
            />
            <InputLabel htmlFor="password" sx={{ fontSize: 14 }}>
              Password
            </InputLabel>
            <PasswordField
              id="password"
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              autoComplete="current-password"
              disabled={loading}
              error={!!loginError}
              placeholder="Enter your password"
              value={password}
              onChange={(e: any) => handlePasswordChange(e.target.value)}
            />

            {/* Error/Success Messages */}
            {loginError && (
              <Typography
                variant="caption"
                sx={{
                  mt: 1,
                  display: "block",
                  color: "error.main",
                  fontSize: 12,
                  textAlign: "center",
                }}
              >
                {loginError}
              </Typography>
            )}

            {magicLinkError && (
              <Typography
                variant="caption"
                sx={{
                  mt: 1,
                  display: "block",
                  color: "error.main",
                  fontSize: 12,
                  textAlign: "center",
                }}
              >
                {magicLinkError}
              </Typography>
            )}

            {magicLinkSuccess && (
              <Typography
                variant="caption"
                sx={{
                  mt: 1,
                  display: "block",
                  color: "success.main",
                  fontSize: 12,
                  textAlign: "center",
                }}
              >
                {magicLinkSuccess}
              </Typography>
            )}
            <Box
              sx={{
                "& a": { fontSize: 10 },
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <FormGroup sx={{ "& .MuiTypography-root": { fontSize: 10 } }}>
                <FormControlLabel
                  sx={{ fontSize: 10 }}
                  control={
                    <Checkbox
                      id="remember"
                      checked={remember}
                      onChange={(e: any) => setRemember(e.target.checked)}
                      size="small"
                      disabled={loading}
                    />
                  }
                  label="Remember for 90 days"
                />
              </FormGroup>
              <Link
                onClick={handleNavigate("/forgot-password")}
                to="/forgot-password"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                Forgot Password?
              </Link>
            </Box>

            <Button
              type="submit"
              sx={{ mt: 1 }}
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading || !email.trim()}
            >
              {password && password.trim().length > 0
                ? "Login"
                : "Send Secure Link"}
            </Button>
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                display: "block",
                textAlign: "center",
                color: "text.secondary",
                fontSize: 12,
              }}
            >
              {password && password.trim().length > 0
                ? "Click to login with email and password"
                : "Leave password empty to receive a secure login link via email"}
            </Typography>

            {/* Self-registration is hidden for now; the /signup route stays in
                place so it can be turned back on without rebuilding it.
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ mt: 2, fontSize: 14 }}
              >
                Don't have an account?{" "}
                <Link
                  onClick={handleNavigate("/signup")}
                  to="/signup"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    fontWeight: "bold",
                    fontFamily: "Brandon_Grotesque_bold",
                  }}
                >
                  Create Account
                </Link>
              </Typography>
            </Box>
            */}
          </Box>
        </Grid>
        <Grid
          size={{ sm: 12, md: 6 }}
          sx={{
            display: { xs: "none", md: "block" },
            height: "100vh",
            overflow: "hidden",
            borderRadius: 0,
          }}
        >
          <Box
            sx={{
              position: "relative",
              height: "100vh",
              width: "100%",
              overflow: "hidden",
            }}
          >
            {SIGN_IN_PHOTOS.map((src, index) => (
              <img
                key={src}
                src={src}
                alt=""
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  opacity: index === photoIndex ? 1 : 0,
                  transition: "opacity 1.4s ease",
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>
    </Fade>
  );
};

export default LoginForm;
